import type { JSX } from 'react';
import type { CustomControlCode } from '@common/enums';
import type { IProcessActivityReadSection } from '@common/interfaces/processActivityReadSection';

export type RegistryEntry<DataType> = {
    ButtonElement?: () => JSX.Element;
    canCopyReadSections: boolean;
    EditElement?: () => JSX.Element;
    hideEditActions: boolean;
    hideReadActions: boolean;
    /**
     * @deprecated register in common registry
     */
    initialData?: DataType;
    ReadElement?: () => JSX.Element;
    readSectionsTransformer?: (readSections: IProcessActivityReadSection[]) => IProcessActivityReadSection[];
};

// `any` is necessary here due to the generic `DataType` for RegistryEntry
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VersionMap = Map<number, RegistryEntry<any>>;

type Registry = Map<CustomControlCode, VersionMap>;

/**
 * CustomControlRegistry is a singleton and can only be created once.
 */
export class CustomControlRegistry {
    private static _instance: CustomControlRegistry;
    private readonly registry: Registry;

    private constructor() {
        this.registry = new Map();
    }

    public static get instance(): CustomControlRegistry {
        if (!this._instance) {
            this._instance = new CustomControlRegistry();
        }

        return this._instance;
    }

    public getRegistry(): Registry {
        return this.registry;
    }

    public register<DataType>({
        ButtonElement,
        EditElement,
        ReadElement,
        canCopyReadSections,
        code,
        initialData,
        hideEditActions,
        hideReadActions,
        readSectionsTransformer,
        version,
    }: Omit<RegistryEntry<DataType>, 'canCopyReadSections' | 'hideEditActions' | 'hideReadActions'> & {
        canCopyReadSections?: boolean;
        code: CustomControlCode;
        hideEditActions?: boolean;
        hideReadActions?: boolean;
        version: number;
    }): void {
        if (this.registry.get(code)?.has(version)) {
            throw new Error(`Custom control "${code}" and version "${version}" was already registered`);
        }
        if (!this.registry.has(code)) {
            this.registry.set(code, new Map());
        }
        this.registry.get(code)?.set(version, {
            ButtonElement,
            EditElement,
            ReadElement,
            canCopyReadSections: Boolean(canCopyReadSections),
            hideEditActions: Boolean(hideEditActions),
            hideReadActions: Boolean(hideReadActions),
            initialData,
            readSectionsTransformer,
        });
    }
}
